import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { AumicoAbcpConfig, AumicoAbcpConfigService } from '../abcp-data.config';
import {
    AccountStateCommand,
    IAbcpSyncBody,
    IAbcpSyncResult,
    IAccountStatePatch,
    IAccountBindingWrite,
    IAccountBindingWriteResult,
    IClassificationSummary,
    IClosingSyncState,
    IPeriodBounds,
    IProjectAccountsResponse,
    IReconciliationStatusResponse,
} from '../models/project-account.model';

/** `start..end`, the shape the route's `comparisonPeriods` parameter takes. */
function asRange(period: IPeriodBounds): string {
    return `${period.periodStart}..${period.periodEnd}`;
}

/**
 * The five calls a closing's account screen makes.
 *
 * All four are scoped by the project in the PATH. Organisation and zone never travel — the backend
 * takes them from the verified token, so sending them would be decoration a caller could get wrong.
 *
 * The list and the counters are separate calls because they cost differently: the list downloads and
 * decrypts one journal PER PERIOD, the counters are one aggregation, and the period list reads
 * metadata only. The screen polls the cheap one.
 */
@Injectable()
export class ProjectAccountsService {
    private readonly projectsEndpoint: string;

    public constructor(
        private readonly http: HttpClient,
        @Inject(AumicoAbcpConfigService) private readonly config: AumicoAbcpConfig,
    ) {
        this.projectsEndpoint = `${config.apiGateway}projects`;
    }

    /**
     * The accounts of one closing, with balances. Expensive — not for polling, and more so per
     * comparison period, since each one is another journal to download and decrypt.
     *
     * The periods are named by their ISO BOUNDS, taken from `getJournalPeriods`. Never by year, and
     * never built from a date held here: after a change of balance-sheet date two periods can end in
     * the same calendar year, and a year would then name neither of them.
     *
     * Omitting the parameter is not the same as sending an empty one — omitted, the backend resolves
     * the prior year itself, which is what a screen that has not chosen anything wants.
     *
     * `withZeroAccounts` keeps accounts that are zero in every period answered. Off by default,
     * matching the route: a chart carries positions a client never posts to, and a screen of `0.00`
     * rows buries the accounts that matter.
     */
    public getAccounts(
        projectId: string,
        comparisonPeriods: readonly IPeriodBounds[] = [],
        withZeroAccounts = false,
    ): Observable<IProjectAccountsResponse> {
        const params: Record<string, string> = {};
        if (comparisonPeriods.length > 0) {
            params['comparisonPeriods'] = comparisonPeriods.map(asRange).join(',');
        }
        // Sent only when switched ON. The route's default is to leave zero accounts out, and sending
        // `false` explicitly would say the same thing in a way a reader has to check.
        if (withZeroAccounts) {
            params['withZeroAccounts'] = 'true';
        }

        return this.http.get<IProjectAccountsResponse>(`${this.projectsEndpoint}/${projectId}/accounts`, { params });
    }

    /**
     * Which periods the backend HOLDS a journal for. Cheap — metadata only, no journal.
     *
     * This is what the comparison chips are built from, and the reason they are honest: it reports our
     * own storage rather than claiming to know which fiscal years the ERP has. Abacus cannot answer
     * the latter at all.
     *
     * It also carries WHEN this closing last pulled. On this route because the header needs it on
     * every tab, and this is the only project-scoped call the closing page makes on every tab — the
     * projects list, where the timestamp also travels, is not loaded on this screen at all.
     */
    public getJournalPeriods(projectId: string): Observable<IClosingSyncState> {
        return this.http.get<IClosingSyncState>(`${this.projectsEndpoint}/${projectId}/accounts/journals`);
    }

    /** The counters. Cheap, and the only call the screen may poll. */
    public getClassificationSummary(projectId: string): Observable<IClassificationSummary> {
        return this.http.get<IClassificationSummary>(
            `${this.projectsEndpoint}/${projectId}/accounts/classification-status`,
        );
    }

    /**
     * The reconciliation counters (#160) — cheap, like the classification counters. The only route
     * `ProjectAccountsEffects.pollReconciliation$` (#162) may poll; `getAccounts()` above stays
     * off-limits to it for the same reason it is off-limits to the classification poll.
     */
    public getReconciliationStatus(projectId: string): Observable<IReconciliationStatusResponse> {
        return this.http.get<IReconciliationStatusResponse>(
            `${this.projectsEndpoint}/${projectId}/accounts/reconciliation-status`,
        );
    }

    /**
     * Assign positions by hand.
     *
     * The provenance is NOT sent: the backend stamps `MANUAL` at the door, because anyone reaching
     * this route did so with a user's token. A body field claiming otherwise would be believed.
     */
    public writeBindings(projectId: string, bindings: IAccountBindingWrite[]): Observable<IAccountBindingWriteResult> {
        return this.http.post<IAccountBindingWriteResult>(`${this.projectsEndpoint}/${projectId}/accounts/bindings`, {
            bindings,
        });
    }

    /**
     * Pull the ERP for this closing and store what comes back.
     *
     * Neither the company nor the closing's own period is sent — both come from the project. What the
     * client alone knows is the connection, the vendor-side client id, and which comparison periods it
     * would like filled in.
     */
    public sync(projectId: string, body: IAbcpSyncBody): Observable<IAbcpSyncResult> {
        return this.http.post<IAbcpSyncResult>(`${this.projectsEndpoint}/${projectId}/abcp/sync`, body);
    }

    /**
     * REV-22005 — a trustee's change to ONE account's state: the review flag set or taken off, or the
     * document value entered by hand (`entscheidregister.md` N-2.2).
     *
     * Neither the author nor a timestamp is sent — the backend takes both from the verified token and
     * its own clock, so a body cannot back-date a flag or put a colleague's name on it. The answer is
     * the row's new state, for the reducer to patch in place.
     */
    public writeAccountState(
        projectId: string,
        accountId: string,
        command: AccountStateCommand,
    ): Observable<IAccountStatePatch> {
        const account = `${this.projectsEndpoint}/${projectId}/accounts/${accountId}`;

        switch (command.kind) {
            case 'reviewFlag':
                return this.http.put<IAccountStatePatch>(`${account}/review-flag`, { reason: command.reason });
            case 'reviewUnflag':
                // Idempotent on the server: taking off a marker that is not there answers the state.
                return this.http.delete<IAccountStatePatch>(`${account}/review-flag`);
            case 'documentBalance':
                return this.http.put<IAccountStatePatch>(`${account}/document-balance`, {
                    amountMinor: command.amountMinor,
                    currency: command.currency,
                    note: command.note,
                });
            case 'documentBalanceClear':
                // Idempotent on the server like the flag: an account without a value answers its state.
                return this.http.delete<IAccountStatePatch>(`${account}/document-balance`);
        }
    }

    /**
     * REV-22007 — import a stored trial balance into this closing (`POST …/abcp/sync/file`).
     *
     * The same tally as the vendor pull. A file the virus scan has not cleared yet answers 409
     * `abcp.fileImport.scanPending`; the effect retries exactly that, with backoff.
     */
    public syncFromFile(projectId: string, fileId: string): Observable<IAbcpSyncResult> {
        return this.http.post<IAbcpSyncResult>(`${this.projectsEndpoint}/${projectId}/abcp/sync/file`, { fileId });
    }
}
